import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, lastValueFrom, Observable, of, Subscription, timer } from 'rxjs';
import { filter, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { REGISTER } from '../../../account/components/register/register.graphql';
import {
    GetOrderByCodeQuery,
    GetOrderByCodeQueryVariables,
    RegisterMutation,
    RegisterMutationVariables
} from '../../../common/generated-types';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { DataService } from '../../../core/providers/data/data.service';
import { StateService } from '../../../core/providers/state/state.service';

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { getRequestBaseUrl } from '../../../common/utils/get-request-base-url';
import { HttpClient } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { ActiveService } from 'src/app/core/providers/active/active.service';
import { CheckoutConfirmationComponentImports } from '../..';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../../core/providers/notification/notification.service';
import { GET_ORDER_BY_CODE } from './checkout-confirmation.graphql';

@Component({
    standalone: true,
    imports: CheckoutConfirmationComponentImports,
    selector: 'vsf-checkout-confirmation',
    templateUrl: './checkout-confirmation.component.html',
    // styleUrls: ['./checkout-confirmation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutConfirmationComponent implements OnInit, OnDestroy {
    private stateService = inject(StateService);
    private dataService = inject(DataService);
    private changeDetector = inject(ChangeDetectorRef);
    private route = inject(ActivatedRoute);
    private activeService = inject(ActiveService);
    private http = inject(HttpClient);
    private notificationService = inject(NotificationService);
    private translocoService = inject(TranslocoService);
    private platformId = inject(PLATFORM_ID);
    private document = inject(DOCUMENT);

    registrationSent = false;
    order$: Observable<GetOrderByCodeQuery['orderByCode']>;
    notFound$: Observable<boolean>;
    isCheckingPayment$: Observable<boolean>;
    isInvoicePayment$: Observable<boolean>;
    private pdfDownloadError = '';
    private shopApiPath = environment.shopApiPath.replace(/^\/+|\/+$/g, '');
    sessionToken: string | null = null;
    private subs = new Subscription();

    ngOnInit() {
        const orderRequest$ = this.route.paramMap.pipe(
            map((paramMap) => paramMap.get('code')),
            filter(notNullOrUndefined),
            switchMap((code) => this.dataService.query<GetOrderByCodeQuery, GetOrderByCodeQueryVariables>(GET_ORDER_BY_CODE, { code })),
            map((data) => data.orderByCode),
            shareReplay({bufferSize: 1, refCount: true})
        );
        this.order$ = orderRequest$.pipe(filter(notNullOrUndefined));
        this.notFound$ = orderRequest$.pipe(map((res) => !res));

        // Stripe response processing
        const stripeRedirect$ = this.route.queryParamMap.pipe(
            map((params) => ({
                redirectStatus: params.get('redirect_status'),
                paymentIntent: params.get('payment_intent')
            })),
            take(1)
        );
        combineLatest([this.order$.pipe(take(1)), stripeRedirect$])
            .pipe(
                tap(([order, stripe]) => {
                    // If the payment is successful immediately
                    if (stripe.redirectStatus === 'succeeded' && order?.state === 'PaymentSettled') {
                        this.activeService.clearActiveOrder();
                        return of(null);
                    }

                    // Successful payment, but missing webhook
                    if (
                        stripe.redirectStatus === 'succeeded' &&
                        (order?.state === 'ArrangingPayment' || order?.state === 'PaymentAuthorized')
                    ) {
                        return timer(0, 3000).pipe(
                            switchMap(() => orderRequest$),
                            filter((refreshedOrder) => refreshedOrder?.state === 'PaymentSettled'),
                            take(1),
                            tap(() => {
                                this.activeService.clearActiveOrder();
                                this.changeDetector.markForCheck();
                            })
                        );
                    }

                    return of(null);
                })
            )
            .subscribe();

        this.isCheckingPayment$ = this.order$.pipe(
            map((order) => !!order && (order.state === 'ArrangingPayment' || order.state === 'PaymentAuthorized'))
        );

        this.isInvoicePayment$ = this.order$.pipe(
            map((order) => {
                if (!order) return false;
                const payments = (order as any).payments;
                return payments?.length > 0 && payments[0].method === 'invoice';
            })
        );

        const translationKeys = ['checkout.confirmation.unableDownloadPdf'];

        const sub = this.translocoService.langChanges$
            .pipe(
                switchMap(() => {
                    const translationObservables = translationKeys.map((key) => this.translocoService.selectTranslate(key));
                    return combineLatest(translationObservables);
                })
            )
            .subscribe((translations: string[]) => {
                [this.pdfDownloadError] = translations;
                this.changeDetector.markForCheck();
            });

        if (isPlatformBrowser(this.platformId)) {
            this.sessionToken = localStorage.getItem('auth_token');
        }

        this.subs.add(sub);
    }

    register() {
        this.order$
            .pipe(
                take(1),
                mergeMap((order) => {
                    const customer = order?.customer;
                    if (customer) {
                        return this.dataService.mutate<RegisterMutation, RegisterMutationVariables>(REGISTER, {
                            input: {
                                emailAddress: customer.emailAddress,
                                firstName: customer.firstName,
                                lastName: customer.lastName
                            }
                        });
                    } else {
                        return of({});
                    }
                })
            )
            .subscribe(() => {
                this.registrationSent = true;
                this.changeDetector.markForCheck();
            });
    }

    async viewInvoiceBase64(order: any) {
        const url = `${getRequestBaseUrl(this.platformId)}/${this.shopApiPath}/invoices/${order?.code}`;
        const token = localStorage.getItem('auth_token');

        try {
            const response = await lastValueFrom(
                this.http.get(url, {
                    responseType: 'arraybuffer',
                    observe: 'response',
                    headers: { Authorization: `Bearer ${token}` }
                })
            );

            // temp
            const serverFilePath = response.headers.get('X-File-Path');
            // console.log('PDF base64 4:', serverFilePath);

            const bytes = new Uint8Array(response.body as ArrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            const checkedWin = this.document.defaultView;
            if (!checkedWin) {
                return;
            }

            const base64String = checkedWin.btoa(binary);
            const dataUrl = `data:application/pdf;base64,${base64String}`;
            const iframeUrl = `${dataUrl}#toolbar=0`;
            const fileName = `invoice-${order?.code}`;

            const newWindow = this.document.defaultView?.open('', '_blank');

            if (newWindow) {
                newWindow.document.title = fileName;
                newWindow.document.body.style.margin = '0';
                newWindow.document.body.style.overflow = 'hidden';
                newWindow.document.body.innerHTML = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>${fileName}</title>
                        <style>
                            body { margin: 0; display: flex; flex-direction: column; height: 100vh; font-family: sans-serif; }
                            .toolbar { 
                                background: #323639; 
                                color: white; 
                                padding: 10px 20px; 
                                display: flex; 
                                justify-content: space-between; 
                                align-items: center;
                                box-shadow: 0 2px 5px rgba(0,0,0,0.3);
                                z-index: 100;
                            }
                            .btn-download { 
                                background: #494d4e;
                                color: white; 
                                border: none; 
                                padding: 8px 16px; 
                                border-radius: 4px; 
                                cursor: pointer; 
                                text-decoration: none;
                                font-size: 14px;
                            }
                            .btn-download:hover { background: #5b5d5e; }
                            iframe { flex-grow: 1; border: none; }
                        </style>
                    </head>
                    <body>
                        <div class="toolbar">
                            <span>${fileName}</span>
                            <a href="${dataUrl}" download="${fileName}" class="btn-download">
                                Download PDF
                            </a>
                        </div>
                        <iframe src="${iframeUrl}"></iframe>
                    </body>
                    </html>
                `;
                newWindow.document.close();
            }
        } catch (error) {
            console.error('Invoice download error:', error);
            this.notificationService.error(this.pdfDownloadError).subscribe();
        }
    }

    async viewInvoice(order: any) {
        const url = `${getRequestBaseUrl(this.platformId)}/${this.shopApiPath}/invoices/${order?.code}`;
        const token = localStorage.getItem('auth_token');

        try {
            const response = await lastValueFrom(
                this.http.get(url, {
                    responseType: 'blob',
                    observe: 'response',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            // temporary, for the testing
            const serverFilePath = response.headers.get('X-File-Path');
            // console.log('PDF path4:', serverFilePath);

            // temporary, for the testing
            // console.log(response);

            const blob = response.body;
            if (!blob) {
                throw new Error('Response body is empty');
            }

            const checkedWin = this.document.defaultView;
            if (!checkedWin) {
                return;
            }

            const blobUrl = checkedWin.URL.createObjectURL(blob);
            this.document.defaultView?.open(blobUrl, '_blank');
        } catch (error) {
            console.error('Invoice download error:', error);
            this.notificationService.error(this.pdfDownloadError).subscribe();
        }
    }

    async viewInvoiceSaveAs(order: any) {
        const url = `${getRequestBaseUrl(this.platformId)}/${this.shopApiPath}/invoices/${order?.code}`;
        const token = localStorage.getItem('auth_token');

        try {
            const response = await lastValueFrom(
                this.http.get(url, {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            const checkedWin = this.document.defaultView;
            if (!checkedWin) {
                return;
            }


            const blob = new Blob([response], { type: 'application/pdf' });
            const blobUrl = checkedWin.URL.createObjectURL(blob);
            const fileName = `invoice-${order?.code}.pdf`;

            // temporary, for the testing
            // console.log('Blob response:', response.size, response.type);

            const win = this.document.defaultView?.open(blobUrl, '_blank');
            if (win) {
                win.document.title = fileName;
            }

            const link = this.document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            this.document.body.appendChild(link);
            link.click();
            this.document.body.removeChild(link);

            setTimeout(() => checkedWin.URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            console.error('Invoice download error:', error);
            this.notificationService.error(this.pdfDownloadError).subscribe();
        }
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
}
